import { EditingInput } from 'components/EditingInput'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import React, { FC, useEffect, useState } from 'react'
import { styled } from 'styled-components'
import { KVMeasurement, KVMeasurementSegment } from 'types'

type Unit = 'Meters' | 'Feet' | 'Inches'

// Define the custom event type
interface UnitChangeEvent extends CustomEvent {
  detail: Unit
}

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
`

const inputStyles = {
  fontSize: '12px',
  padding: '2px 8px',
  width: '65px',
  height: '26px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: 'white',
} as React.CSSProperties

const UnitLabel = styled.span`
  font-size: 14px;
  color: white;
  opacity: 0.8;
`

export const MeasurementSegmentLengthInput: FC<{
  selectedMeasurement: KVMeasurement
  selectedMeasurementSegment: KVMeasurementSegment
}> = ({ selectedMeasurement, selectedMeasurementSegment }) => {
  const { sendCommand } = useKestrelViewerContext()
  const [currentUnit, setCurrentUnit] = useState<Unit>('Meters')
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Listen for unit changes from the ToggleUnitsButton
  useEffect(() => {
    // Type guard to check if an event is a UnitChangeEvent
    function isUnitChangeEvent(event: Event): event is UnitChangeEvent {
      return event instanceof CustomEvent && 'detail' in event
    }

    function handleUnitChange(this: Window, ev: Event) {
      if (isUnitChangeEvent(ev)) {
        // Now TypeScript knows ev is UnitChangeEvent
        setCurrentUnit(ev.detail)
      }
    }

    window.addEventListener('unitChange', handleUnitChange)
    return () => {
      window.removeEventListener('unitChange', handleUnitChange)
    }
  }, [])

  const handleLengthChange = (value: string) => {
    setInputValue(value)

    // Allow empty input
    if (value === '') {
      setError(null)
      return
    }

    // Check if input is a valid number
    if (!/^\d*\.?\d*$/.test(value)) {
      setError('Please enter a valid number')
      return
    }

    const numValue = parseFloat(value)

    // Check if number is greater than 0
    if (numValue <= 0) {
      setError('Value must be greater than 0')
      return
    }

    // If we get here, the input is valid
    setError(null)
    sendCommand({
      command: 'updateMeasurement',
      measurementId: selectedMeasurement.id,
      measurementSegmentId: selectedMeasurementSegment.measurementSegmentId,
      measurementSegmentValue: numValue,
    })
  }

  return (
    <>
      <InputContainer>
        <EditingInput
          key={selectedMeasurementSegment.measurementSegmentId}
          value={inputValue}
          setValue={handleLengthChange}
          style={{
            ...inputStyles,
            borderColor: error ? '#ff4d4f' : '#ccc',
          }}
        />
        <UnitLabel>{currentUnit.toLowerCase()}</UnitLabel>
      </InputContainer>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </>
  )
}
